// Faq2.js - Contains functions for creating a modern FAQ section with tabs
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a modern Frequently Asked Questions section with tabs
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the FAQ section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The FAQ section elements
 */
export const createFaqElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const faqSectionClass = 'faq-section-tabs';
  const faqContainerClass = 'faq-container-tabs';
  const faqHeaderClass = 'faq-header-tabs';
  const faqTitleClass = 'faq-title-tabs';
  const faqSubtitleClass = 'faq-subtitle-tabs';
  const faqContentClass = 'faq-content-tabs';
  const faqTabsClass = 'faq-tabs';
  const faqTabClass = 'faq-tab';
  const faqTabActiveClass = 'faq-tab-active';
  const faqTabContentClass = 'faq-tab-content';
  const faqTabContentActiveClass = 'faq-tab-content-active';
  const faqQuestionClass = 'faq-question-tabs';
  const faqAnswerClass = 'faq-answer-tabs';
  
  // Define styles
  // Desktop Styles (default)
  const faqSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f0f9ff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const faqContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1000px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const faqHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '40px',
    maxWidth: '800px'
  };

  const faqTitleStyles = {
    fontSize: '44px',
    fontWeight: '800',
    marginBottom: '20px',
    color: '#0f172a',
    lineHeight: '1.2',
    background: 'linear-gradient(90deg, #0ea5e9, #2563eb)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  };

  const faqSubtitleStyles = {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#475569',
    maxWidth: '700px'
  };

  const faqContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: '#ffffff'
  };

  const faqTabsStyles = {
    display: 'flex',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    justifyContent: 'center',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    overflow: 'hidden',
    gap: '2px'
  };

  const faqTabStyles = {
    padding: '16px 28px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: '#f0f7ff'
  };

  const faqTabActiveStyles = {
    color: '#0ea5e9',
    borderBottom: '3px solid #0ea5e9',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 4px rgba(14, 165, 233, 0.1)'
  };

  const faqTabContentStyles = {
    display: 'none',
    padding: '32px',
    backgroundColor: '#ffffff'
  };

  const faqTabContentActiveStyles = {
    display: 'block'
  };

  const faqQuestionStyles = {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '12px',
    display: 'block'
  };

  const faqAnswerStyles = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#475569',
    marginBottom: '24px',
    display: 'block'
  };

  // Tablet Styles
  const tabletFaqTitleStyles = {
    fontSize: '38px'
  };

  const tabletFaqTabsStyles = {
    flexWrap: 'wrap'
  };

  // Mobile Styles
  const mobileFaqTitleStyles = {
    fontSize: '32px'
  };

  const mobileFaqTabStyles = {
    padding: '12px 16px',
    fontSize: '14px',
    width: '100%'
  };

  const mobileFaqTabsStyles = {
    flexDirection: 'column',
    width: '100%'
  };

  const mobileFaqQuestionStyles = {
    fontSize: '16px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(faqSectionClass, faqSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(faqContainerClass, faqContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(faqHeaderClass, faqHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTitleClass, faqTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqSubtitleClass, faqSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(faqContentClass, faqContentStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTabsClass, faqTabsStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTabClass, faqTabStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTabActiveClass, faqTabActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTabContentClass, faqTabContentStyles);
    newRegistry[DEVICES.DESKTOP].set(faqTabContentActiveClass, faqTabContentActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(faqQuestionClass, faqQuestionStyles);
    newRegistry[DEVICES.DESKTOP].set(faqAnswerClass, faqAnswerStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(faqTitleClass, tabletFaqTitleStyles);
    newRegistry[DEVICES.TABLET].set(faqTabsClass, tabletFaqTabsStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(faqTitleClass, mobileFaqTitleStyles);
    newRegistry[DEVICES.MOBILE].set(faqTabClass, mobileFaqTabStyles);
    newRegistry[DEVICES.MOBILE].set(faqTabsClass, mobileFaqTabsStyles);
    newRegistry[DEVICES.MOBILE].set(faqQuestionClass, mobileFaqQuestionStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    faqSectionClass,
    faqContainerClass,
    faqHeaderClass,
    faqTitleClass,
    faqSubtitleClass,
    faqContentClass,
    faqTabsClass,
    faqTabClass,
    faqTabActiveClass,
    faqTabContentClass,
    faqTabContentActiveClass,
    faqQuestionClass,
    faqAnswerClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const contentId = Date.now() + 5;
  const tabsId = Date.now() + 6;
  const tabsContentId = Date.now() + 7;
  
  // Tab 1 - General Questions
  const tab1Id = Date.now() + 8;
  const tabContent1Id = Date.now() + 9;
  
  // General Questions content
  const question1Id = Date.now() + 10;
  const answer1Id = Date.now() + 11;
  const question2Id = Date.now() + 12;
  const answer2Id = Date.now() + 13;

  // Tab 2 - Services
  const tab2Id = Date.now() + 14;
  const tabContent2Id = Date.now() + 15;
  
  // Services content
  const question3Id = Date.now() + 16;
  const answer3Id = Date.now() + 17;
  const question4Id = Date.now() + 18;
  const answer4Id = Date.now() + 19;

  // Tab 3 - Pricing
  const tab3Id = Date.now() + 20;
  const tabContent3Id = Date.now() + 21;
  
  // Pricing content
  const question5Id = Date.now() + 22;
  const answer5Id = Date.now() + 23;
  const question6Id = Date.now() + 24;
  const answer6Id = Date.now() + 25;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'FAQ Section Tabs',
      parentId: null,
      className: faqSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'faq-section-tabs',
        'data-component': 'section',
        'data-section-type': 'faq',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Container Tabs',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: faqContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'faq-container-tabs'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Header Tabs',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: faqHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'faq-header-tabs'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'FAQ Title Tabs',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: faqTitleClass,
      styles: {},
      children: [],
      text: 'Questions & Answers',
      attributes: {
        'data-element': 'faq-title-tabs'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'FAQ Subtitle Tabs',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: faqSubtitleClass,
      styles: {},
      children: [],
      text: 'Browse through our categorized FAQs to find answers to your questions and learn more about our services.',
      attributes: {
        'data-element': 'faq-subtitle-tabs'
      }
    },
    
    // Content wrapper
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Content Tabs',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: faqContentClass,
      styles: {},
      children: [tabsId, tabsContentId],
      text: '',
      attributes: {
        'data-element': 'faq-content-tabs'
      }
    },
    
    // Tabs container
    {
      id: tabsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Tabs',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: faqTabsClass,
      styles: {},
      children: [tab1Id, tab2Id, tab3Id],
      text: '',
      attributes: {
        'data-element': 'faq-tabs'
      }
    },
    
    // Tabs content container
    {
      id: tabsContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'FAQ Tabs Content',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: 'faq-tabs-content',
      styles: {},
      children: [tabContent1Id, tabContent2Id, tabContent3Id],
      text: '',
      attributes: {
        'data-element': 'faq-tabs-content'
      }
    },
    
    // Tab 1
    {
      id: tab1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'General Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: `${faqTabClass} ${faqTabActiveClass}`,
      styles: {},
      children: [],
      text: 'General Questions',
      attributes: {
        'data-element': 'faq-tab',
        'data-tab': 'tab-1',
        'data-active': 'true'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1000,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: faqTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: faqTabContentActiveClass
            },
            label: `Tab Click: ${faqTabActiveClass} & ${faqTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab 2
    {
      id: tab2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Services Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: faqTabClass,
      styles: {},
      children: [],
      text: 'Services',
      attributes: {
        'data-element': 'faq-tab',
        'data-tab': 'tab-2',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1001,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: faqTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: faqTabContentActiveClass
            },
            label: `Tab Click: ${faqTabActiveClass} & ${faqTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab 3
    {
      id: tab3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Pricing Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: faqTabClass,
      styles: {},
      children: [],
      text: 'Pricing',
      attributes: {
        'data-element': 'faq-tab',
        'data-tab': 'tab-3',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1002,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: faqTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: faqTabContentActiveClass
            },
            label: `Tab Click: ${faqTabActiveClass} & ${faqTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab Content 1 - General Questions
    {
      id: tabContent1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'General Tab Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: `${faqTabContentClass} ${faqTabContentActiveClass}`,
      styles: {},
      children: [question1Id, answer1Id, question2Id, answer2Id],
      text: '',
      attributes: {
        'data-element': 'faq-tab-content',
        'data-tab-content': 'tab-1',
        'data-active': 'true'
      }
    },
    
    // Question 1 (General)
    {
      id: question1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Question 1',
      parentId: tabContent1Id,
      hierarchyName: getHierarchyName('h4'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'How secure is the website builder platform?',
      attributes: {
        'data-element': 'faq-question-tabs'
      }
    },
    
    // Answer 1 (General)
    {
      id: answer1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Answer 1',
      parentId: tabContent1Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Our platform implements enterprise-grade security measures including SSL encryption, regular security audits, and automated backup systems. We use industry-standard protocols to protect your data and ensure your website remains secure. Additionally, we provide built-in security features like DDoS protection and malware scanning to keep your website safe.',
      attributes: {
        'data-element': 'faq-answer-tabs'
      }
    },
    
    // Question 2 (General)
    {
      id: question2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Question 2',
      parentId: tabContent1Id,
      hierarchyName: getHierarchyName('h4'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'Can I transfer my existing website to your platform?',
      attributes: {
        'data-element': 'faq-question-tabs'
      }
    },
    
    // Answer 2 (General)
    {
      id: answer2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Answer 2',
      parentId: tabContent1Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Yes, we offer multiple ways to transfer your existing website. You can either use our automated import tool that works with popular platforms like WordPress, Wix, and Shopify, or our team can assist with a manual migration. We ensure all your content, images, and SEO settings are properly transferred to maintain your online presence.',
      attributes: {
        'data-element': 'faq-answer-tabs'
      }
    },
    
    // Tab Content 2 - Services
    {
      id: tabContent2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services Tab Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: faqTabContentClass,
      styles: {},
      children: [question3Id, answer3Id, question4Id, answer4Id],
      text: '',
      attributes: {
        'data-element': 'faq-tab-content',
        'data-tab-content': 'tab-2',
        'data-active': 'false'
      }
    },
    
    // Question 3 (Services)
    {
      id: question3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Question 3',
      parentId: tabContent2Id,
      hierarchyName: getHierarchyName('h4'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'What kind of design customization services do you offer?',
      attributes: {
        'data-element': 'faq-question-tabs'
      }
    },
    
    // Answer 3 (Services)
    {
      id: answer3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Answer 3',
      parentId: tabContent2Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Our design services range from template customization to fully custom website designs. We offer professional design consultation, brand integration, custom graphics creation, and responsive design optimization. Our team can help with color schemes, typography, layout adjustments, and creating unique interactive elements that match your brand identity.',
      attributes: {
        'data-element': 'faq-answer-tabs'
      }
    },
    
    // Question 4 (Services)
    {
      id: question4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Question 4',
      parentId: tabContent2Id,
      hierarchyName: getHierarchyName('h4'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'Do you provide SEO optimization services?',
      attributes: {
        'data-element': 'faq-question-tabs'
      }
    },
    
    // Answer 4 (Services)
    {
      id: answer4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Answer 4',
      parentId: tabContent2Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Yes, we provide comprehensive SEO services including keyword research, on-page optimization, meta tag optimization, and content strategy development. Our SEO experts can help improve your search engine rankings, optimize your content structure, and implement technical SEO best practices. We also provide regular performance reports and ongoing optimization recommendations.',
      attributes: {
        'data-element': 'faq-answer-tabs'
      }
    },
    
    // Tab Content 3 - Pricing
    {
      id: tabContent3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Tab Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: faqTabContentClass,
      styles: {},
      children: [question5Id, answer5Id, question6Id, answer6Id],
      text: '',
      attributes: {
        'data-element': 'faq-tab-content',
        'data-tab-content': 'tab-3',
        'data-active': 'false'
      }
    },
    
    // Question 5 (Pricing)
    {
      id: question5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Question 5',
      parentId: tabContent3Id,
      hierarchyName: getHierarchyName('h4'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'What payment methods do you accept?',
      attributes: {
        'data-element': 'faq-question-tabs'
      }
    },
    
    // Answer 5 (Pricing)
    {
      id: answer5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Answer 5',
      parentId: tabContent3Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers. For business plans, we also offer invoice-based payments and can accommodate corporate purchasing processes. All payments are processed securely through industry-leading payment gateways with encryption and fraud protection.',
      attributes: {
        'data-element': 'faq-answer-tabs'
      }
    },
    
    // Question 6 (Pricing)
    {
      id: question6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Question 6',
      parentId: tabContent3Id,
      hierarchyName: getHierarchyName('h4'),
      className: faqQuestionClass,
      styles: {},
      children: [],
      text: 'Can I upgrade or downgrade my plan later?',
      attributes: {
        'data-element': 'faq-question-tabs'
      }
    },
    
    // Answer 6 (Pricing)
    {
      id: answer6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Answer 6',
      parentId: tabContent3Id,
      hierarchyName: getHierarchyName('p'),
      className: faqAnswerClass,
      styles: {},
      children: [],
      text: 'Yes, you can change your plan at any time. When upgrading, you\'ll only pay the prorated difference for the remaining period. When downgrading, we\'ll apply any unused credit to your next billing cycle. All your website data and customizations are preserved when switching plans, and the change takes effect immediately.',
      attributes: {
        'data-element': 'faq-answer-tabs'
      }
    }
  ];

  return {
    faqSection: newElements[0],
    elements: newElements,
  };
};

export default createFaqElements; 